import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  Divider,
  UnorderedList,
  ListItem,
  Link,
} from "@chakra-ui/react";

const LAST_UPDATED = "May 27, 2026";

const TermsAndConditions = () => {
  return (
    <Box bg="gray.50" minH="100vh" py={10}>
      <Container
        maxW="4xl"
        shadow="md"
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="md"
      >
        <Stack spacing={6}>
          <Heading as="h1" size="xl">
            Terms and Conditions
          </Heading>

          <Text color="gray.600">Last updated: {LAST_UPDATED}</Text>

          <Divider />

          <Text>
            Welcome to <strong>PickDengu</strong> and{" "}
            <strong>PickDengu Chat</strong>. These Terms and Conditions
            (“Terms”) govern your access to and use of our website, dashboard,
            WhatsApp services, Meta integrations, commerce tools, delivery
            tools, booking tools, catalog tools, and related services available
            through{" "}
            <Link href="https://pickdengu.mw" color="blue.600" isExternal>
              https://pickdengu.mw
            </Link>
            .
          </Text>

          <Text>
            By accessing or using PickDengu, creating an account, connecting a
            Meta business portfolio, messaging us on WhatsApp, placing an order,
            making a booking, listing products, or using our business tools, you
            agree to these Terms.
          </Text>

          <Heading as="h2" size="md">
            1. About PickDengu
          </Heading>

          <Text>
            PickDengu is a WhatsApp-first commerce, delivery, booking, catalog,
            and business automation platform. PickDengu may support online
            shopping, seller tools, product catalogs, WhatsApp notifications,
            customer support, deliveries, hikes/trips, accommodation, car hire,
            invoices, payouts, and related business services.
          </Text>

          <Heading as="h2" size="md">
            2. Eligibility and Accounts
          </Heading>

          <UnorderedList pl={6}>
            <ListItem>
              You must provide accurate, current, and complete information when
              using PickDengu.
            </ListItem>
            <ListItem>
              You are responsible for maintaining the confidentiality of your
              account credentials.
            </ListItem>
            <ListItem>
              Businesses are responsible for ensuring that their representatives
              have authority to connect business assets, WhatsApp accounts, and
              product catalogs.
            </ListItem>
            <ListItem>
              You must not impersonate another person or business or use the
              platform for unauthorized purposes.
            </ListItem>
          </UnorderedList>

          <Heading as="h2" size="md">
            3. Acceptable Use
          </Heading>

          <Text>You agree not to:</Text>

          <UnorderedList pl={6}>
            <ListItem>
              Use PickDengu for unlawful, fraudulent, abusive, or harmful
              activity.
            </ListItem>
            <ListItem>
              Send spam, scams, misleading content, or unauthorized marketing.
            </ListItem>
            <ListItem>
              Attempt to access systems, accounts, tokens, catalogs, or data
              without authorization.
            </ListItem>
            <ListItem>
              Interfere with the security, reliability, or operation of the
              platform.
            </ListItem>
            <ListItem>
              Upload or list prohibited, illegal, counterfeit, unsafe, or
              restricted products.
            </ListItem>
            <ListItem>
              Misuse WhatsApp, Meta, catalog, payment, delivery, or support
              features.
            </ListItem>
          </UnorderedList>

          <Heading as="h2" size="md">
            4. WhatsApp and Messaging Services
          </Heading>

          <Text>
            PickDengu may use WhatsApp and other messaging channels to send
            order confirmations, payment updates, booking confirmations,
            delivery updates, verification codes, invoices, reminders, customer
            support messages, and other service-related messages.
          </Text>

          <Text>
            By providing your WhatsApp number, messaging PickDengu, or opting in
            through a business using PickDengu, you agree to receive relevant
            service messages. Message frequency may vary. You may opt out of
            non-essential messages by replying <strong>STOP</strong> or
            contacting support@pickdengu.mw.
          </Text>

          <Heading as="h2" size="md">
            5. Meta Business, WhatsApp Business and Catalog Integrations
          </Heading>

          <Text>
            Businesses may choose to connect Meta business assets, WhatsApp
            Business Accounts, WhatsApp phone numbers, and product catalogs to
            PickDengu Chat. By connecting these assets, you confirm that you are
            authorized to grant access and that PickDengu may use the granted
            permissions to provide requested services.
          </Text>

          <Text>
            PickDengu may use authorized access to read WhatsApp Business
            Account information, read phone number profile details, send
            approved WhatsApp templates, retrieve catalog products, create or
            update catalog products, and synchronize product data between
            PickDengu and connected Meta catalog tools.
          </Text>

          <Text>
            You are responsible for complying with Meta’s terms, WhatsApp
            Business Platform rules, product catalog rules, messaging policies,
            and all applicable laws when using these integrations.
          </Text>

          <Heading as="h2" size="md">
            6. Product Catalogs, Sellers and Listings
          </Heading>

          <Text>
            Sellers and businesses are responsible for the accuracy, legality,
            availability, pricing, images, descriptions, and fulfillment of
            products or services they list on PickDengu or synchronize to a
            connected catalog.
          </Text>

          <Text>
            PickDengu may remove, disable, reject, or modify listings that
            appear inaccurate, unsafe, prohibited, unlawful, unavailable, or in
            breach of these Terms or platform policies.
          </Text>

          <Heading as="h2" size="md">
            7. Orders, Bookings and Transactions
          </Heading>

          <Text>
            Orders, bookings, car hire, accommodation, hikes/trips, delivery
            requests, and other transactions are subject to availability,
            confirmation, pricing, delivery conditions, seller acceptance, and
            payment status.
          </Text>

          <Text>
            PickDengu may cancel, reject, pause, refund, or investigate
            transactions where there is suspected fraud, pricing error,
            unavailable stock, safety risk, misuse, or operational failure.
          </Text>

          <Heading as="h2" size="md">
            8. Payments, Fees, Payouts and Invoices
          </Heading>

          <Text>
            PickDengu may support payments, invoices, seller allocations,
            delivery fees, driver earnings, and payout workflows. Fees,
            commissions, payout timing, and settlement rules may vary by
            service, seller agreement, or business arrangement.
          </Text>

          <Text>
            Users, sellers, drivers, and businesses are responsible for
            providing accurate payment and tax information where required.
          </Text>

          <Heading as="h2" size="md">
            9. Delivery, Dispatch and Location
          </Heading>

          <Text>
            PickDengu may coordinate delivery, pickup, warehouse dispatch,
            driver assignment, route estimation, status updates, and proof of
            delivery. Delivery times, costs, route estimates, and availability
            are not guaranteed and may be affected by location accuracy,
            traffic, weather, operational capacity, and third-party services.
          </Text>

          <Heading as="h2" size="md">
            10. Privacy and Data Protection
          </Heading>

          <Text>
            Our use of personal information, Meta-connected data, WhatsApp data,
            business data, catalog data, and transaction data is described in
            our Privacy Policy:
          </Text>

          <Text>
            <Link
              href="https://pickdengu.mw/privacy"
              color="blue.600"
              isExternal
            >
              https://pickdengu.mw/privacy
            </Link>
          </Text>

          <Heading as="h2" size="md">
            11. Third-Party Services
          </Heading>

          <Text>
            PickDengu may integrate with third-party services, including Meta,
            WhatsApp, Firebase, hosting providers, payment providers, mapping
            providers, logistics providers, analytics providers, email
            providers, and other business tools. Your use of third-party
            services may also be governed by their own terms and policies.
          </Text>

          <Heading as="h2" size="md">
            12. Service Availability
          </Heading>

          <Text>
            We aim to keep PickDengu reliable and available, but we do not
            guarantee uninterrupted, secure, or error-free operation. We may
            suspend, modify, restrict, or discontinue any part of the service
            for maintenance, security, operational, legal, or business reasons.
          </Text>

          <Heading as="h2" size="md">
            13. Intellectual Property
          </Heading>

          <Text>
            PickDengu, PickDengu Chat, platform designs, workflows, software,
            logos, text, graphics, and related materials are owned by or
            licensed to PickDengu. You may not copy, modify, distribute, reverse
            engineer, or misuse our intellectual property without permission.
          </Text>

          <Heading as="h2" size="md">
            14. User and Business Content
          </Heading>

          <Text>
            You remain responsible for content, products, images, descriptions,
            messages, business information, catalog data, and other materials
            you submit or connect to PickDengu. You grant PickDengu permission
            to use that content as necessary to provide, operate, display,
            synchronize, and support the services.
          </Text>

          <Heading as="h2" size="md">
            15. Suspension and Termination
          </Heading>

          <Text>
            We may suspend or terminate access to PickDengu if we believe that a
            user, seller, driver, business, or connected account has violated
            these Terms, misused the platform, created risk, violated law,
            breached third-party platform rules, or threatened the security or
            reliability of the service.
          </Text>

          <Heading as="h2" size="md">
            16. Disclaimers
          </Heading>

          <Text>
            PickDengu is provided on an “as is” and “as available” basis. To the
            maximum extent permitted by law, we disclaim warranties of
            merchantability, fitness for a particular purpose, non-infringement,
            availability, accuracy, and uninterrupted operation.
          </Text>

          <Heading as="h2" size="md">
            17. Limitation of Liability
          </Heading>

          <Text>
            To the maximum extent permitted by law, PickDengu will not be liable
            for indirect, incidental, special, consequential, punitive, or lost
            profit damages arising out of or related to your use of the service,
            third-party integrations, delivery delays, catalog errors, messaging
            delays, unavailable products, or unauthorized use of your account.
          </Text>

          <Heading as="h2" size="md">
            18. Changes to These Terms
          </Heading>

          <Text>
            We may update these Terms from time to time. The updated version
            will be posted on this page with a new “Last updated” date.
            Continued use of PickDengu after changes means you accept the
            updated Terms.
          </Text>

          <Heading as="h2" size="md">
            19. Contact Information
          </Heading>

          <Text>
            For questions about these Terms, account access, business
            integrations, Meta disconnection, or support, contact:
          </Text>

          <UnorderedList pl={6}>
            <ListItem>Email: support@pickdengu.mw</ListItem>
            <ListItem>
              Website:{" "}
              <Link href="https://pickdengu.mw" color="blue.600" isExternal>
                https://pickdengu.mw
              </Link>
            </ListItem>
          </UnorderedList>

          <Divider />

          <Text fontSize="sm" color="gray.500">
            © {new Date().getFullYear()} PickDengu. All rights reserved.
          </Text>
        </Stack>
      </Container>
    </Box>
  );
};

export default TermsAndConditions;
